import { ApiError, apiErrorCode } from "@/lib/api";
import {
  messageFor,
  VALIDATION_SUMMARY,
  validationFailures,
  validationReasonCopy,
  type ValidationFailure,
} from "@/lib/errors";
import type { AuthedRequester } from "@/lib/brokerAuth";
import { duplicateEmailMessage } from "@/lib/bondRosterChecks";
import {
  GIA_SIGNER_STATUSES,
  giaSignerStatusLabel,
  type GiaSignerStatus,
} from "@/lib/bondStateModel";
import { formatBondDate } from "@/lib/bondValueDisplay";

// BRK-S182 — the GENERAL INDEMNITY AGREEMENT, as a broker sees it.
//
// Every listed owner and every listed owner's spouse signs the GIA before any
// bond is issued. PMS sends it through DocuSign (PMS-S483), and this portal
// NEVER talks to DocuSign: the three own-scoped BFF routes below (PMS-S484) are
// its only path, and the order read carries a small `gia` summary beside them.
//
// ── WHAT A BROKER MAY SEE, AND NOTHING MORE ─────────────────────────────────
//
// The status, the signers' names and the emails the broker typed. No document
// id, no envelope id, no DocuSign status text and no surety-side recipient —
// PMS publishes none of them on a broker read, and nothing here declares a home
// for one (`test/lib/giaDiscretion.test.ts` sweeps `src/` for it). The executed
// PDF is filed on the ACCOUNT as a `gia` document. PMS-S642 makes that type
// broker-visible, so the broker downloads it where every other document lives —
// the account's Documents tab and the submission page's Documents section,
// labelled "General Indemnity Agreement" (BRK-S285) — and never from here.
//
// ── PINNED (PMS-S483 / PMS-S484, adopted ahead of the pin and re-pinned) ─────
//
// The order read's `gia` block is always emitted and is `null` unless the GIA
// applies from this order: since D15 (PMS-S839) it is requested AT FILING, so
// any filed order on the account — referred or not — carries it, and nothing
// filed yet is a 409 `GIA_NOT_APPLICABLE` on the routes. The reader still takes an
// absent key as `null`, which costs nothing. The status vocabulary is CLOSED,
// and a member this build does not know renders a neutral "Status unavailable"
// with nothing actionable, never a blank chip. The routes and shapes are held
// to the pinned contract by `test/lib/giaContract.test.ts`.

const BASE = "/api/broker/bond/submissions";

/** The GIA status vocabulary, closed (PMS-S483). */
export const GIA_STATUSES = [
  "not_required",
  "not_sent",
  "sent",
  "signed",
  "declined",
  "voided",
] as const;
export type GiaStatus = (typeof GIA_STATUSES)[number];

/** Why a GIA is required on this order. `null` when it is not required. */
export const GIA_REQUIRED_REASONS = [
  "first_gia",
  "indemnitors_changed",
] as const;
export type GiaRequiredReason = (typeof GIA_REQUIRED_REASONS)[number];

/** Why a person signs — as an owner, or as a listed owner's spouse. */
export const GIA_SIGNER_BASES = ["owner", "spouse_of"] as const;
export type GiaSignerBasis = (typeof GIA_SIGNER_BASES)[number];

/**
 * The order read's `gia` block (PMS-S483).
 *
 * `signed` is what makes the order READY, and that is a derived fact PMS
 * publishes here rather than a workflow status. `not_required` is derived too:
 * a GIA is already on file and the indemnitors have not changed.
 */
export interface BondGiaSummary {
  status: GiaStatus;
  requiredReason: GiaRequiredReason | null;
  sentAt: string | null;
  signedAt: string | null;
  /** When the last reminder went to the signers who have not signed (BRK-S400);
   *  null when none has. Optional: an older PMS omits it.
   *  PMS-S839 (P4) — `lastReminderAt`. */
  lastReminderAt?: string | null;
}

/** The owner a spouse's seat derives from. `name` is null when the roster
 *  names nobody for that owner. */
export interface GiaSpouseOf {
  ownerId: string;
  name: string | null;
}

/** One person who signs the GIA (PMS-S484). Only LISTED seats are returned. */
export interface GiaSigner {
  /** The person's OWN roster id — the spouse's own on a spouse seat — which
   *  is what `PUT …/gia/signers` addresses. */
  ownerId: string;
  basis: GiaSignerBasis;
  name: string;
  /** Null when nobody has supplied one yet — the broker fills it in. */
  email: string | null;
  /** The owner a SPOUSE signs beside; null on an owner's own seat. */
  spouseOf: GiaSpouseOf | null;
  /** This signer's own progress (BRK-S400), read through
   *  `giaSignerStatusLabel`; absent on an older PMS, which reads "Status
   *  unavailable" rather than guessing from the GIA's status.
   *  PMS-S839 (P4) — `signers[].status`. */
  status?: GiaSignerStatus | null;
  /** When THIS signer signed, ISO; null until they do.
   *  PMS-S839 (P4) — `signers[].signedAt`. */
  signedAt?: string | null;
}

/** `GET …/gia` — the signers and where the GIA stands (PMS-S484). */
export interface BondGia extends BondGiaSummary {
  signers: GiaSigner[];
}

/**
 * A GIA read as `readBondGia` hands it to the page (BRK-S437): the same body,
 * with an unknown status read as null ("Status unavailable") and every field
 * the page touches present. Never the raw wire body.
 */
export interface GiaRead extends Omit<BondGia, "status" | "lastReminderAt"> {
  status: GiaStatus | null;
  lastReminderAt: string | null;
}

/** One confirmed email, as `PUT …/gia/signers` takes it. */
export interface GiaSignerEmail {
  ownerId: string;
  email: string;
}

/** A status this build knows, or null for one it does not (or none at all). */
export function readGiaStatus(raw: unknown): GiaStatus | null {
  if (typeof raw !== "string") return null;
  return GIA_STATUSES.find((s) => s === raw) ?? null;
}

const STATUS_LABELS: Record<GiaStatus, string> = {
  not_required: "Not required",
  not_sent: "Not sent",
  sent: "Sent",
  signed: "Signed",
  declined: "Declined — message Signal",
  voided: "Voided",
};

/** The chip's words. An unrecognised status is said to be unavailable —
 *  never a blank, and never a guess at the nearest known one. */
export function giaStatusLabel(status: GiaStatus | null): string {
  return status === null ? "Status unavailable" : STATUS_LABELS[status];
}

/** Emails are the broker's to confirm only before the GIA goes out. */
export function giaIsEditable(status: GiaStatus | null): boolean {
  return status === "not_sent";
}

/**
 * The seat's basis, in words: "Owner", or "Spouse of {owner name}" off the
 * contract's `spouseOf`. A spouse whose owner is unnamed reads plain "Spouse" —
 * the name is never guessed from a neighbouring row.
 */
export function giaBasisLabel(signer: GiaSigner): string {
  if (signer.basis === "owner") return "Owner";
  const named = signer.spouseOf?.name;
  return named ? `Spouse of ${named}` : "Spouse";
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const GIA_EMAIL_MISSING =
  "Enter an email address — every signer needs one.";
export const GIA_EMAIL_INVALID = "Enter a valid email address.";

/**
 * The client half of the server's three refusals, keyed by ownerId: every
 * signer has an email, each one is shaped like an email, and no two signers
 * share one (case-insensitively). The server is the authority; this saves the
 * round trip and puts each reason on the row it is about.
 */
export function validateGiaEmails(
  emails: readonly GiaSignerEmail[],
): Record<string, string> {
  const errors: Record<string, string> = {};
  const byAddress = new Map<string, string[]>();
  for (const { ownerId, email } of emails) {
    const text = email.trim();
    if (text === "") {
      errors[ownerId] = GIA_EMAIL_MISSING;
      continue;
    }
    if (!EMAIL_SHAPE.test(text)) {
      errors[ownerId] = GIA_EMAIL_INVALID;
      continue;
    }
    const key = text.toLowerCase();
    byAddress.set(key, [...(byAddress.get(key) ?? []), ownerId]);
  }
  for (const [address, owners] of byAddress) {
    if (owners.length < 2) continue;
    // EVERY row that shares it: three people on one address is one mistake,
    // and naming two of them leaves a send that fails again.
    for (const ownerId of owners) {
      errors[ownerId] = duplicateEmailMessage(address);
    }
  }
  return errors;
}

/**
 * The refusals that name SIGNERS (400), each carrying
 * `details.fields: [{ownerId, reason}]` where `reason` is a CODE —
 * `missing | invalid | duplicate | unknown` — rather than a sentence.
 */
export const GIA_SIGNER_ROW_CODES = [
  "GIA_SIGNER_EMAIL_DUPLICATE",
  "GIA_SIGNER_EMAIL_INVALID",
  "GIA_SIGNER_EMAIL_MISSING",
  "GIA_SIGNER_UNKNOWN",
] as const;

export const GIA_EMAIL_DUPLICATE =
  "This email is already used by someone else on this request. Each person needs their own email address.";
export const GIA_SIGNER_UNKNOWN =
  "This person is no longer on the request. Reload the page to see who signs.";

/** The sentence for each `details.fields[].reason` code. */
const ROW_REASON: Readonly<Record<string, string>> = {
  missing: GIA_EMAIL_MISSING,
  invalid: GIA_EMAIL_INVALID,
  duplicate: GIA_EMAIL_DUPLICATE,
  unknown: GIA_SIGNER_UNKNOWN,
};

/** The sentence when the row's reason code is absent or unrecognised. */
const CODE_REASON: Record<(typeof GIA_SIGNER_ROW_CODES)[number], string> = {
  GIA_SIGNER_EMAIL_DUPLICATE: GIA_EMAIL_DUPLICATE,
  GIA_SIGNER_EMAIL_INVALID: GIA_EMAIL_INVALID,
  GIA_SIGNER_EMAIL_MISSING: GIA_EMAIL_MISSING,
  GIA_SIGNER_UNKNOWN: GIA_SIGNER_UNKNOWN,
};

/**
 * Refusals about the GIA itself that the broker cannot fix from this screen —
 * the order's values are missing something the agreement needs
 * (`GIA_VALUES_MISSING`), there are more signers than the template has
 * seats for (`GIA_SIGNER_SLOTS_EXCEEDED`), or a married owner's template has
 * no spouse seat (`GIA_SPOUSE_SLOT_MISSING`, BRK-S189 — its `details.slot`
 * names the role and is never shown). Said plainly, with who to ask, rather
 * than in the server's words.
 */
export const GIA_CONTACT_UNDERWRITER =
  "We can’t send this GIA from here. Please message Signal.";
const CONTACT_UNDERWRITER_CODES = [
  "GIA_VALUES_MISSING",
  "GIA_SIGNER_SLOTS_EXCEEDED",
  "GIA_SPOUSE_SLOT_MISSING",
];

export interface GiaServerErrors {
  /** A reason per signer row, keyed by ownerId. */
  byOwner: Record<string, string>;
  /** The line beside the button — set when nothing could go on a row. */
  summary: string | null;
}

/**
 * A refused save or send, placed on the rows it names.
 *
 * The four signer codes land on their rows, each reason code turned into a
 * sentence. A field naming a signer this section is not drawing, or a refusal
 * that names none (the 409s), becomes the summary instead — a reason on the
 * wrong row is worse than one beside the button.
 */
export function giaServerErrors(
  err: unknown,
  ownerIds: readonly string[],
): GiaServerErrors {
  // Never the server's text for a refusal without a code the portal knows
  // (BRK-S279): that is how "signers: Invalid input" reached a broker.
  const fallback =
    err instanceof ApiError
      ? messageFor(err)
      : "We couldn’t send the GIA. Please try again.";
  const code = apiErrorCode(err);
  if (code !== null && CONTACT_UNDERWRITER_CODES.includes(code)) {
    return { byOwner: {}, summary: GIA_CONTACT_UNDERWRITER };
  }
  const invalid = validationFailures(err);
  if (invalid !== null) return giaValidationErrors(invalid, ownerIds);
  const known = GIA_SIGNER_ROW_CODES.find((c) => c === code);
  if (!known || !(err instanceof ApiError)) {
    return { byOwner: {}, summary: fallback };
  }
  const details = (err.body as { details?: { fields?: unknown } } | null)
    ?.details;
  const fields = Array.isArray(details?.fields) ? details.fields : [];
  const drawn = new Set(ownerIds);
  const byOwner: Record<string, string> = {};
  for (const entry of fields) {
    if (typeof entry !== "object" || entry === null) continue;
    const { ownerId, reason } = entry as {
      ownerId?: unknown;
      reason?: unknown;
    };
    if (typeof ownerId !== "string" || !drawn.has(ownerId)) continue;
    byOwner[ownerId] =
      (typeof reason === "string" ? ROW_REASON[reason] : undefined) ??
      CODE_REASON[known];
  }
  return Object.keys(byOwner).length > 0
    ? { byOwner, summary: null }
    : { byOwner, summary: fallback };
}

/**
 * A `VALIDATION_FAILED` on the signers save, placed on rows where it can be.
 *
 * `signers.<i>.<field>` names the i-th signer SENT, and the section sends
 * exactly the rows it draws, in order — so the index is the row. Anything else
 * (`signers` itself, the SB-01619 empty list; a stray key; an index past the
 * rows) has no row and becomes the portal's own summary sentence.
 */
function giaValidationErrors(
  invalid: readonly ValidationFailure[],
  ownerIds: readonly string[],
): GiaServerErrors {
  const byOwner: Record<string, string> = {};
  let unplaced = invalid.length === 0;
  for (const { path, reason } of invalid) {
    const row = /^signers\.(\d+)(\.|$)/.exec(path);
    const ownerId = row ? ownerIds[Number(row[1])] : undefined;
    if (ownerId === undefined) {
      unplaced = true;
      continue;
    }
    byOwner[ownerId] ??= validationReasonCopy(reason);
  }
  return { byOwner, summary: unplaced ? VALIDATION_SUMMARY : null };
}

// ── THE TOLERANT READ (BRK-S437) ─────────────────────────────────────────────
//
// Every GIA body — the GET and each write's read-back — goes through
// `readBondGia` before the page touches it. A body without `signers` once took
// the whole submission page down (`gia.signers.map` on undefined); now it is a
// GIA with nobody listed, and a signer row the page cannot address (no string
// `ownerId`, a basis outside the closed pair) is dropped rather than guessed.

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const textOrNull = (v: unknown): string | null =>
  typeof v === "string" ? v : null;

/** What a signer's `name` reads when the roster names nobody. */
export const NAME_NOT_ON_FILE = "Name not on file";

function readSigner(raw: unknown): GiaSigner | null {
  if (!isRecord(raw) || typeof raw.ownerId !== "string") return null;
  const basis = GIA_SIGNER_BASES.find((b) => b === raw.basis);
  if (basis === undefined) return null;
  const spouseOf =
    isRecord(raw.spouseOf) && typeof raw.spouseOf.ownerId === "string"
      ? { ownerId: raw.spouseOf.ownerId, name: textOrNull(raw.spouseOf.name) }
      : null;
  return {
    ownerId: raw.ownerId,
    basis,
    name: textOrNull(raw.name) ?? NAME_NOT_ON_FILE,
    email: textOrNull(raw.email),
    spouseOf,
    status: GIA_SIGNER_STATUSES.find((s) => s === raw.status) ?? null,
    signedAt: textOrNull(raw.signedAt),
  };
}

/** A GIA body as the page may use it — see THE TOLERANT READ above. */
export function readBondGia(raw: unknown): GiaRead {
  const body = isRecord(raw) ? raw : {};
  const signers = Array.isArray(body.signers) ? body.signers : [];
  return {
    status: readGiaStatus(body.status),
    requiredReason:
      GIA_REQUIRED_REASONS.find((r) => r === body.requiredReason) ?? null,
    sentAt: textOrNull(body.sentAt),
    signedAt: textOrNull(body.signedAt),
    lastReminderAt: textOrNull(body.lastReminderAt),
    signers: signers.map(readSigner).filter((s): s is GiaSigner => s !== null),
  };
}

// ── THE GIA FORM DIALOG'S WORDS (BRK-S423) ───────────────────────────────────

/** A signer's chip: "Signed m/d/yyyy", "Not signed", or "Status unavailable"
 *  for a status this build does not know or PMS did not serve. */
export function giaSignerChip(signer: Pick<GiaSigner, "status" | "signedAt">): {
  label: string;
  signed: boolean;
} {
  const label = giaSignerStatusLabel(signer.status);
  if (signer.status !== "signed") return { label, signed: false };
  return {
    label: signer.signedAt
      ? `${label} ${formatBondDate(signer.signedAt)}`
      : label,
    signed: true,
  };
}

/** "Sent m/d/yyyy · Last reminder m/d/yyyy", off the served dates only; null
 *  before the GIA has gone out. */
export function giaSentLine(
  gia: Pick<GiaRead, "sentAt" | "lastReminderAt">,
): string | null {
  if (!gia.sentAt) return null;
  const sent = `Sent ${formatBondDate(gia.sentAt)}`;
  return gia.lastReminderAt
    ? `${sent} · Last reminder ${formatBondDate(gia.lastReminderAt)}`
    : sent;
}

/** Whether a reminder has anyone to go to: a signer not served as signed. */
export function giaHasUnsigned(signers: readonly GiaSigner[]): boolean {
  return signers.some((s) => s.status !== "signed");
}

const giaPath = (id: string) => `${BASE}/${encodeURIComponent(id)}/gia`;

/** GET …/submissions/{id}/gia — the signers and the status. Own-scoped. */
export async function getBondGia(
  request: AuthedRequester,
  id: string,
): Promise<GiaRead> {
  return readBondGia(await request<unknown>(giaPath(id)));
}

/**
 * PUT …/submissions/{id}/gia/signers — store the confirmed emails. Answers the
 * same read as the GET; the section does not move its chip on it.
 *
 * `pushToAccount` is the wizard's choice, asked again here (PMS-S484; the
 * contract defaults it to true): checked, the confirmed emails also update the account's people;
 * unchecked, they stay with this GIA. No push outcome is reported back to the
 * broker — the notice the wizard used to show was retired in BRK-S181
 * revision 8, and the same rule holds here.
 */
export async function saveGiaSigners(
  request: AuthedRequester,
  id: string,
  signers: readonly GiaSignerEmail[],
  pushToAccount: boolean,
): Promise<GiaRead> {
  return readBondGia(
    await request<unknown>(`${giaPath(id)}/signers`, {
      method: "PUT",
      body: { signers, pushToAccount },
    }),
  );
}

/** POST …/submissions/{id}/gia/send — send for signature. Idempotent: a
 *  repeat while `sent` or `signed` answers 200 with the same read. */
export async function sendBondGia(
  request: AuthedRequester,
  id: string,
): Promise<GiaRead> {
  return readBondGia(
    await request<unknown>(`${giaPath(id)}/send`, { method: "POST" }),
  );
}

/**
 * POST …/submissions/{id}/gia/remind — email the signers who have not signed
 * again (BRK-S423). Answers the GET's body with `lastReminderAt` moved.
 * PMS-S839 (P4); refused with 409 `GIA_NOT_OUT` when no GIA is out.
 */
export async function remindBondGia(
  request: AuthedRequester,
  id: string,
): Promise<GiaRead> {
  return readBondGia(
    await request<unknown>(`${giaPath(id)}/remind`, { method: "POST" }),
  );
}

/**
 * POST …/submissions/{id}/gia/resend — VOID the GIA that is out and send a
 * fresh one to the same signers (BRK-S423): everyone signs the new one, so
 * the read-back carries a new `sentAt` and every signer unsigned. `send` is
 * idempotent and cannot do this.
 * PMS-S839 (P4) — void-and-resend; the admin void is PMS-S485. PMS voids
 * the envelope that is out BEFORE sending the fresh one, so a refused send
 * reads `voided` and a plain send recovers it.
 */
export async function resendBondGia(
  request: AuthedRequester,
  id: string,
): Promise<GiaRead> {
  return readBondGia(
    await request<unknown>(`${giaPath(id)}/resend`, { method: "POST" }),
  );
}
